<template>
  <div class="date-picker-container">
    <AlternativeSelect
      v-if="showDays"
      v-model="day"
      filterable
      :items="days"
      :placeholder="$t('DAY')"
      :key-prop="dayItem => dayItem"
      :value-prop="dayItem => dayItem"
      :label-prop="dayItem => dayItem"
      class="date-picker-item"
    />

    <AlternativeSelect
      v-model="month"
      filterable
      :items="months"
      :placeholder="$t('MONTH')"
      class="date-picker-item"
    />

    <AlternativeSelect
      v-model="year"
      filterable
      :items="years"
      :placeholder="$t('YEAR')"
      :key-prop="yearItem => yearItem"
      :value-prop="yearItem => yearItem"
      :label-prop="yearItem => yearItem"
      class="date-picker-item"
    />
  </div>
</template>

<script>
import moment from 'moment-jalaali';
import lang from '@/mixins/lang';
import AlternativeSelect from '@@/common/AlternativeSelect/';
import { calendars, gregorianMonths, jalaliMonths } from '@/constants/date';
export default {
  components: { AlternativeSelect },
  mixins: [lang],
  props: {
    startYear: {
      default: 2010,
      type: Number,
    },
    endYear: {
      default: new Date().getFullYear(),
      type: Number,
    },
    defaultDate: {
      default: null,
      type: Number,
    },
    calendarType: {
      type: String,
      validator(value) {
        return [calendars.JALALI, calendars.GREGORIAN].includes(value);
      },
      default: null,
    },
    format: {
      type: String,
      default() {
        return this.calendarType === calendars.JALALI
          ? 'jYYYY-jMM-jDD'
          : 'YYYY-MM-DD';
      },
    },
  },
  data() {
    return {
      day: '',
      month: '',
      year: '',
    };
  },

  computed: {
    type() {
      let typeByLang =
        this.lang === 'fa' ? calendars.JALALI : calendars.GREGORIAN;
      return this.calendarType || typeByLang;
    },
    date() {
      if (this.day && this.month && this.year) {
        return `${this.year}-${this.month}-${this.day}`;
      }

      return '';
    },
    showDays() {
      return this.format.toLowerCase().includes('d');
    },
    years() {
      const rangeLength = this.endYear - this.startYear + 1;
      let range = Array(rangeLength)
        .fill(this.startYear)
        .map((year, index) => this.startYear + index);

      if (this.type === calendars.JALALI) {
        // @TODO remove the endYear in favor of the current year
        // Jalaali year start 2-3 months after Gregorian year
        const currentJalaaliYear = moment(this.endYear + 1, 'YYYY').jYear();
        const startJalaliYear = currentJalaaliYear - rangeLength + 1;

        range = Array(rangeLength)
          .fill(startJalaliYear)
          .map((year, index) => startJalaliYear + index);
      }

      return range;
    },

    months() {
      const monthsItems = [];
      for (let m = 1; m <= 12; m++) {
        const monthLabel =
          this.type === calendars.JALALI
            ? jalaliMonths[m - 1]
            : gregorianMonths[m - 1];

        monthsItems.push({
          label: monthLabel,
          value: m,
          key: monthLabel,
        });
      }

      return monthsItems;
    },

    maxDaysInMonth() {
      if (this.type === calendars.JALALI) {
        return moment.jDaysInMonth(this.year, this.month - 1);
      }

      return moment(this.date, this.format).daysInMonth();
    },

    days() {
      return Array.from({ length: this.maxDaysInMonth }, (_, i) => i + 1);
    },
  },
  watch: {
    date() {
      if (!this.date) {
        return false;
      }

      let _moment;
      if (this.type === calendars.JALALI) {
        _moment = moment(this.date, 'jYYYY-jMM-jDD');
      } else {
        _moment = moment(this.date, 'YYYY-MM-DD');
      }

      this.$emit('select-date', _moment.format(this.format));
    },

    month() {
      this.day = Math.min(this.maxDaysInMonth, this.day);
    },

    lang(newLang) {
      this.$nextTick(() => {
        if (this.calendarType) return;
        if (newLang === 'fa') {
          this.setFADateModels(this.date);
        } else {
          this.setEnDateModels(this.date, 'jYYYY-jMM-jDD');
        }
      });
    },

    calendarType(newType) {
      if (newType === calendars.JALALI) {
        this.setFADateModels(this.date, 'YYYY-MM-DD');
      } else {
        this.setEnDateModels(this.date, 'jYYYY-jMM-jDD');
      }
    },

    defaultDate: {
      immediate: true,
      handler(date) {
        if (!date) {
          return false;
        }

        if (this.type === calendars.JALALI) {
          this.setFADateModels(date);
        } else {
          this.setEnDateModels(date);
        }
      },
    },
  },

  methods: {
    setFADateModels(dateObj, format) {
      const date = moment(dateObj, format);

      this.day = date.jDate();

      // According to moment docs, month start from zero index
      // +1 is necessary for opting correct month form translation
      // http://momentjs.com/docs/#/get-set/month/
      this.month = date.jMonth() + 1;

      this.year = date.jYear();
    },

    setEnDateModels(dateObj, format) {
      const date = moment(dateObj, format);

      this.day = date.date();

      // According to moment docs, month start from zero index
      // +1 is necessary for opting correct month form translation
      // http://momentjs.com/docs/#/get-set/month/
      this.month = date.month() + 1;

      this.year = date.year();
    },
  },
};
</script>

<style lang="scss">
.date-picker {
  &-container {
    display: flex;
    flex-direction: column;
  }

  &-item {
    flex: 1;
  }

  &-item:not(:last-child) {
    margin-bottom: $global-margin / 2;
  }

  @include breakpoint(medium) {
    &-item:not(:last-child) {
      margin-bottom: 0;
    }

    &-container {
      flex-direction: row;
    }

    &-item + &-item {
      margin-left: $global-margin;

      @include rtl() {
        margin-left: 0;
        margin-right: $global-margin;
      }
    }
  }
}
</style>
